import React, { useState, useEffect, useCallback } from 'react';
import { Clock, ChefHat, CheckCircle, Play, Users, MapPin, Wifi, WifiOff, RefreshCw, Truck, Store as StoreIcon, Utensils } from 'lucide-react';
import { sendKDSReady } from '../utils/messagingIntegration';
import { API_ENDPOINTS } from '../config/apiRouting';

interface KitchenDisplaySystemProps {
  user: any;
  storeId?: string;
}

interface Store {
  id: string;
  name: string;
  address?: string;
  enterpriseId?: string;
}

interface KDSOrder {
  id: string;
  tableNumber: string;
  orderNumber: string;
  items: Array<{
    name: string;
    quantity: number;
    specialInstructions?: string;
  }>;
  assignedServer: {
    id: string;
    name: string;
  };
  status: 'cooking' | 'ready' | 'served';
  orderTime: Date;
  estimatedReady: Date;
  orderType: 'dine-in' | 'pickup' | 'delivery';
  customerName?: string;
  isOnlineOrder: boolean;
}


const KitchenDisplaySystem: React.FC<KitchenDisplaySystemProps> = ({
  user,
  storeId: defaultStoreId = 'demo-store'
}) => {
  const [selectedStoreId, setSelectedStoreId] = useState(defaultStoreId);
  const [stores, setStores] = useState<Store[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [orders, setOrders] = useState<KDSOrder[]>([]);
  const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set());

  // Fetch online orders from the API
  const fetchOnlineOrders = useCallback(async () => {
    if (selectedStoreId === 'demo-store') return;

    setLoadingOrders(true);
    try {
      // Fetch orders that are sent to kitchen but not yet served
      const response = await fetch(`${API_ENDPOINTS.posSyncOrders()}?storeId=${selectedStoreId}&status=sent_to_kitchen,in_progress,ready`);

      if (response.ok) {
        const data = await response.json();
        const apiOrders = data.orders || data.data || [];

        // Transform API orders to KDS format
        const kdsOrders: KDSOrder[] = apiOrders.map((order: any) => ({
          id: order.checkId || order.orderId || order.id,
          tableNumber: order.tableNumber || order.orderType || 'Online',
          orderNumber: order.orderNumber || `#${(order.checkId || order.id || '').slice(-6).toUpperCase()}`,
          items: (order.items || []).map((item: any) => ({
            name: item.name || item.menuItemName,
            quantity: item.quantity || 1,
            specialInstructions: item.specialInstructions || item.notes
          })),
          assignedServer: {
            id: order.serverId || 'online-order',
            name: order.serverName || (order.orderType === 'delivery' ? 'Delivery' : order.orderType === 'pickup' ? 'Pickup' : 'Online')
          },
          status: order.status === 'ready' ? 'ready' : order.status === 'served' ? 'served' : 'cooking',
          orderTime: new Date(order.createdAt || order.orderTime || Date.now()),
          estimatedReady: new Date(order.estimatedReadyTime || Date.now() + 15 * 60000),
          orderType: order.orderType || 'dine-in',
          customerName: order.customerInfo?.name || order.customerName,
          isOnlineOrder: order.isOnlineOrder || order.orderType === 'pickup' || order.orderType === 'delivery'
        }));

        // Merge with existing orders (keep demo orders if any)
        setOrders(prev => {
          const demoOrders = prev.filter(o => o.id.startsWith('ord-'));
          return [...demoOrders, ...kdsOrders];
        });
              }
    } catch (error) {
      console.error('Failed to fetch online orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedStoreId]);

  // Set up WebSocket connection for real-time updates
  useEffect(() => {
    if (selectedStoreId === 'demo-store') return;

    const wsUrl = API_ENDPOINTS.messaging?.websocketUrl;
    if (!wsUrl) {
      // Fall back to polling if WebSocket not available
      const pollInterval = setInterval(fetchOnlineOrders, 30000); // Poll every 30 seconds
      return () => clearInterval(pollInterval);
    }

    let ws: WebSocket | null = null;

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('KDS WebSocket connected');
        setIsConnected(true);
        // Subscribe to kitchen updates
        ws?.send(JSON.stringify({
          action: 'subscribe',
          storeId: selectedStoreId,
          type: 'kitchen_updates'
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_order' || data.type === 'order_update') {
            // Refresh orders when new order comes in
            fetchOnlineOrders();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('KDS WebSocket disconnected');
        setIsConnected(false);
      };

      ws.onerror = (error) => {
        console.error('KDS WebSocket error:', error);
        setIsConnected(false);
      };
    } catch (error) {
      console.error('Failed to connect KDS WebSocket:', error);
    }

    return () => {
      ws?.close();
    };
  }, [selectedStoreId, fetchOnlineOrders]);

  // Initial fetch when store changes
  useEffect(() => {
    fetchOnlineOrders();
  }, [selectedStoreId, fetchOnlineOrders]);

  // Fetch available stores
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.stores());
        if (response.ok) {
          const data = await response.json();
          const storesData = Array.isArray(data) ? data : (data.stores || data.data || []);
          
          // Add demo store if not in the list
          const demoStore = { id: 'demo-store', name: 'Demo Store', address: '123 Demo St' };
          const storeExists = storesData.some((store: Store) => store.id === 'demo-store');
          if (!storeExists) {
            storesData.unshift(demoStore);
          }
          
          setStores(storesData);
        } else {
          setStores([{ id: 'demo-store', name: 'Demo Store', address: '123 Demo St' }]);
        }
      } catch (error) {
        console.error('Failed to fetch stores:', error);
        setStores([{ id: 'demo-store', name: 'Demo Store', address: '123 Demo St' }]);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, []);

  const markOrderReady = async (order: KDSOrder) => {
    setProcessingOrders(prev => new Set(prev.add(order.id)));

    try {
      // If it's an online order, update the order status via API
      if (order.isOnlineOrder && !order.id.startsWith('ord-')) {
        try {
          await fetch(`${API_ENDPOINTS.posSyncOrders()}/${order.id}/status`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              status: 'ready',
              updatedBy: user?.getUsername() || 'kitchen-system',
              timestamp: new Date().toISOString()
            })
          });
        } catch (apiError) {
          console.error('Failed to update order status via API:', apiError);
        }
      }

      // Send KDS Ready alert to the assigned server (for dine-in orders)
      // For online orders, this can also trigger customer notifications
      const result = await sendKDSReady({
        storeId: selectedStoreId,
        tableNumber: order.isOnlineOrder ? (order.customerName || order.orderType) : order.tableNumber,
        orderNumber: order.orderNumber,
        assignedServerId: order.assignedServer.id,
        assignedServerName: order.assignedServer.name,
        senderId: user?.getUsername() || 'kitchen-system',
        senderName: 'Kitchen Display',
        metadata: {
          orderType: order.orderType,
          customerName: order.customerName,
          isOnlineOrder: order.isOnlineOrder
        }
      });

      if (result.success) {
        // Update order status to ready
        setOrders(prevOrders =>
          prevOrders.map(o =>
            o.id === order.id
              ? { ...o, status: 'ready' as const }
              : o
          )
        );

        // Auto-remove after 30 seconds (simulate bump)
        setTimeout(() => {
          setOrders(prevOrders =>
            prevOrders.map(o =>
              o.id === order.id
                ? { ...o, status: 'served' as const }
                : o
            )
          );
        }, 30000);
      }
    } catch (error) {
      console.error('Failed to mark order ready:', error);
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(order.id);
        return newSet;
      });
    }
  };

  const addNewOrder = () => {
    const tableNumbers = ['1', '3', '6', '9', '11', '14', '16', '18'];
    const servers = [
      { id: 'server-alex', name: 'Alex' },
      { id: 'server-jordan', name: 'Jordan' },
      { id: 'server-casey', name: 'Casey' },
      { id: 'server-taylor', name: 'Taylor' }
    ];
    const menuItems = [
      'Grilled Salmon', 'Beef Tenderloin', 'Chicken Alfredo', 'Vegetarian Pasta',
      'BBQ Ribs', 'Shrimp Scampi', 'Pork Chops', 'Lobster Tail'
    ];
    const orderTypes: Array<'dine-in' | 'pickup' | 'delivery'> = ['dine-in', 'pickup', 'delivery'];
    const selectedOrderType = orderTypes[Math.floor(Math.random() * orderTypes.length)];
    const isOnline = selectedOrderType !== 'dine-in';

    const newOrder: KDSOrder = {
      id: `ord-${Date.now()}`,
      tableNumber: isOnline ? selectedOrderType.charAt(0).toUpperCase() + selectedOrderType.slice(1) : tableNumbers[Math.floor(Math.random() * tableNumbers.length)],
      orderNumber: `ORD-${String(Date.now()).slice(-3)}`,
      items: [
        { name: menuItems[Math.floor(Math.random() * menuItems.length)], quantity: 1 },
        { name: 'House Salad', quantity: 1 },
        { name: 'Water', quantity: 2 }
      ],
      assignedServer: isOnline
        ? { id: 'online-order', name: selectedOrderType === 'delivery' ? 'Delivery' : 'Pickup' }
        : servers[Math.floor(Math.random() * servers.length)],
      status: 'cooking',
      orderTime: new Date(),
      estimatedReady: new Date(Date.now() + Math.random() * 15 * 60000 + 5 * 60000),
      orderType: selectedOrderType,
      customerName: isOnline ? ['John D.', 'Maria S.', 'Alex K.', 'Sam W.'][Math.floor(Math.random() * 4)] : undefined,
      isOnlineOrder: isOnline
    };

    setOrders(prev => [...prev, newOrder]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'cooking': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ready': return 'bg-green-100 text-green-800 border-green-200';
      case 'served': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTimeElapsed = (orderTime: Date) => {
    const elapsed = Math.floor((Date.now() - orderTime.getTime()) / 60000);
    return `${elapsed}m`;
  };

  const activeOrders = orders.filter(order => order.status !== 'served');

  return (
    <div className="modern-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <ChefHat className="w-6 h-6 text-orange-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Kitchen Display System</h2>
          {/* Connection Status */}
          <div className={`ml-4 flex items-center text-xs px-2 py-1 rounded-full ${
            isConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {isConnected ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
            {isConnected ? 'Live' : 'Polling'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchOnlineOrders}
            disabled={loadingOrders}
            className="btn-modern-secondary flex items-center"
            title="Refresh orders"
          >
            <RefreshCw className={`w-4 h-4 ${loadingOrders ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={addNewOrder}
            className="btn-modern-secondary flex items-center"
          >
            <Play className="w-4 h-4 mr-2" />
            Add Demo Order
          </button>
        </div>
      </div>

      <p className="text-gray-600 mb-6">
        Manage kitchen orders and communicate order readiness to front-of-house staff. Online orders appear automatically. When orders are marked ready, customers and servers are notified.
      </p>

      {/* Store Selection */}
      <div className="form-group">
        <label className="form-label flex items-center">
          <MapPin className="w-4 h-4 mr-2" />
          Kitchen Location
        </label>
        {loadingStores ? (
          <div className="flex items-center justify-center p-3 border border-gray-300 rounded-md bg-gray-50">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
            Loading stores...
          </div>
        ) : (
          <select
            value={selectedStoreId}
            onChange={(e) => setSelectedStoreId(e.target.value)}
            className="form-control"
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} {store.address && `- ${store.address}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {activeOrders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ChefHat className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <h3 className="text-lg font-medium mb-2">No Active Orders</h3>
          <p className="text-sm mb-4">Click "Add Demo Order" to simulate new orders coming into the kitchen</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {activeOrders.map((order) => (
            <div
              key={order.id}
              className={`border-2 rounded-lg p-4 transition-all shadow-sm ${getStatusColor(order.status)}`}
            >
              {/* Order Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    {order.isOnlineOrder ? (
                      order.orderType === 'delivery' ? (
                        <Truck className="w-5 h-5" />
                      ) : (
                        <StoreIcon className="w-5 h-5" />
                      )
                    ) : (
                      <Utensils className="w-5 h-5" />
                    )}
                    <h4 className="text-lg font-semibold">
                      {order.isOnlineOrder ? order.customerName || order.orderType : `Table ${order.tableNumber}`}
                    </h4>
                  </div>
                  <p className="text-sm opacity-75">#{order.orderNumber}</p>
                  {order.isOnlineOrder && (
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-1 ${
                      order.orderType === 'delivery'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {order.orderType.charAt(0).toUpperCase() + order.orderType.slice(1)}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex items-center text-sm opacity-75 mb-1">
                    <Clock className="w-3 h-3 mr-1" />
                    {getTimeElapsed(order.orderTime)}
                  </div>
                  <div className="text-xs capitalize font-medium px-2 py-1 rounded-full bg-current bg-opacity-20">
                    {order.status}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-4">
                <h5 className="font-medium text-sm mb-2">Order Items:</h5>
                <ul className="text-sm space-y-1">
                  {order.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="w-2 h-2 rounded-full bg-current opacity-50 mr-3 mt-1.5 flex-shrink-0"></div>
                      <div>
                        <span>{item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}</span>
                        {item.specialInstructions && (
                          <span className="block text-xs opacity-60 italic">{item.specialInstructions}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Server Assignment */}
              <div className="flex items-center text-sm opacity-75 mb-4 p-2 bg-white bg-opacity-50 rounded">
                <Users className="w-4 h-4 mr-2" />
                <span className="font-medium">Server: {order.assignedServer.name}</span>
              </div>

              {/* Action Button */}
              {order.status === 'cooking' && (
                <button
                  onClick={() => markOrderReady(order)}
                  disabled={processingOrders.has(order.id)}
                  className={`w-full flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-all ${
                    processingOrders.has(order.id)
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {processingOrders.has(order.id) ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Notifying Server...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark Order Ready
                    </>
                  )}
                </button>
              )}

              {order.status === 'ready' && (
                <div className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-green-700 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Ready for Pickup!
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* System Information */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Kitchen Display System Status</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-600">
          <div>
            <p><strong>Kitchen Location:</strong> {selectedStoreId}</p>
            <p><strong>Store Name:</strong> {stores.find(s => s.id === selectedStoreId)?.name || 'Unknown'}</p>
            <p><strong>Active Orders:</strong> {activeOrders.length} orders in kitchen</p>
          </div>
          <div>
            <p><strong>User:</strong> {user?.getUsername() || 'kitchen-system'}</p>
            <p><strong>Integration:</strong> Real-time server notifications</p>
            <p><strong>Auto-clear:</strong> Orders removed after 30s when ready</p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            <strong>How it works:</strong> When you mark an order ready, an instant alert is sent to the assigned server via the restaurant messaging system. 
            The server will see the notification in their alert bar and can respond immediately.
          </p>
        </div>
      </div>
    </div>
  );
};

export default KitchenDisplaySystem;